import * as Sentry from "@sentry/capacitor";
import * as SentryReact from "@sentry/react";
import { Capacitor } from "@capacitor/core";

export function initSentry() {
  // Não enviar eventos do ambiente de desenvolvimento (localhost) para o Sentry.
  // O Fast Refresh do Vite dispara erros espúrios durante o hot-reload (ex.:
  // "useCompany must be used within a CompanyProvider") que poluem o projeto e
  // afogam erros reais de produção. Defina VITE_SENTRY_DEV=1 para forçar em dev.
  if (import.meta.env.DEV && !import.meta.env.VITE_SENTRY_DEV) {
    console.info("[Sentry] dev environment, skipping init (set VITE_SENTRY_DEV=1 to enable)");
    return;
  }

  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) {
    if (import.meta.env.DEV) console.info("[Sentry] no DSN, skipping init");
    return;
  }
  // Localized cast for the whole options object: @sentry/capacitor pins
  // @sentry/core@10.60 while @sentry/react resolves to 10.66, so
  // `browserTracingIntegration()` yields a nominally-distinct `Integration` (TS2322);
  // additionally the browser-replay sample-rate options aren't declared on capacitor's
  // stricter `CapacitorOptions` (TS2353) even though init forwards them to SentryReact.init.
  // Runtime is correct — this cast keeps the config unchanged.
  Sentry.init(
    {
      dsn,
      environment: import.meta.env.MODE,
      release: `orcamigo@${import.meta.env.VITE_APP_VERSION ?? "dev"}`,
      dist: Capacitor.getPlatform(),
      tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
      replaysSessionSampleRate: 0, // opt-in mais tarde
      replaysOnErrorSampleRate: import.meta.env.PROD ? 0.1 : 0,
      integrations: [
        SentryReact.browserTracingIntegration(),
      ],
      beforeSend(event) {
        // Não vazar tokens de acesso do Supabase em URLs
        if (event.request?.url?.includes("access_token")) {
          event.request.url = event.request.url.replace(
            /access_token=[^&]+/g,
            "access_token=REDACTED"
          );
        }
        return event;
      },
      beforeBreadcrumb(breadcrumb) {
        // Redact sensitive tokens in breadcrumbs (OAuth access tokens & storage signed URLs)
        if (breadcrumb.data?.url && typeof breadcrumb.data.url === "string") {
          if (breadcrumb.data.url.includes("access_token")) {
            breadcrumb.data.url = breadcrumb.data.url.replace(
              /access_token=[^&]+/g,
              "access_token=REDACTED"
            );
          }
          if (
            breadcrumb.data.url.includes("token=") &&
            breadcrumb.data.url.includes("/object/sign/")
          ) {
            breadcrumb.data.url = breadcrumb.data.url.replace(
              /token=[^&]+/g,
              "token=REDACTED"
            );
          }
        }
        return breadcrumb;
      },
    } as unknown as Parameters<typeof Sentry.init>[0],
    SentryReact.init as unknown as Parameters<typeof Sentry.init>[1]
  );
}

export { Sentry, SentryReact };
